/**
 * Wound "Healing" ability move for the Legendary Arena game engine (WP-379).
 *
 * healWounds realizes the printed universal Wound ability — "If you don't
 * recruit or fight anything on your turn, you may KO all the Wounds from your
 * hand." (Universal Rules v23 §Healing Wounds). It KOs every Wound in the
 * current player's hand into G.ko (permanent removal — never back to the wounds
 * supply, never to discard). Wounds are used directly from hand and are never
 * "played".
 *
 * This is a non-core move that gates internally (same pattern as fightVillain /
 * recruitHero / fightMastermind from WP-016 / WP-014A): stage gate, then the
 * block-all pending-choice guards, then a "has not acted this turn" precondition,
 * then mutate G. It creates no pending-choice state (healing is synchronous).
 *
 * No registry imports. No .reduce(). Moves never throw.
 */
import { WOUND_EXT_ID } from '../setup/pilesInit.js';
import { koCard } from '../board/ko.logic.js';
import { recordOwnedCardKoed } from '../hero/deferredConditionalGrants.js';
import { hasPendingKoHeroChoice } from './koHeroChoice.resolve.js';
import { hasPendingScryKoChoice } from './scryKoChoice.resolve.js';
import { hasPendingMelterKoChoice } from './melterKoChoice.resolve.js';
import { hasPendingRuthlessDictatorChoice } from './ruthlessDictatorChoice.resolve.js';
import { hasPendingElectromagneticBubbleChoice } from './electromagneticBubbleChoice.resolve.js';
import { hasPendingDiscardChoice } from './discardChoice.resolve.js';
import { hasPendingPutCardsOnDeckChoice } from './putCardsOnDeckChoice.resolve.js';
import { hasPendingKoDiscardChoice } from './koDiscardChoice.resolve.js';
import { hasPendingReorderChoice } from './reorderChoice.resolve.js';
import { hasPendingDefeatChoice } from './defeatChoice.resolve.js';
import { hasPendingOptionalKoReward } from './optionalKoReward.resolve.js';
import { hasPendingSmashDiscard } from './smashDiscard.resolve.js';
import { hasPendingPutHandOnDeckTop } from './putHandOnDeckTop.resolve.js';
import { hasPendingRevealTopDispose } from './revealTopDispose.resolve.js';
import { hasPendingRevealThreeAssign } from './revealThreeAssign.resolve.js';
import { hasPendingDoOver } from './doOver.resolve.js';
import { hasPendingPlayVillainTopChoice } from './playVillainTop.resolve.js';
import { hasPendingVictoryPileCardPick } from './resolveVictoryPileCardPick.js';
import { hasPendingDrawOrEmpowered } from './drawOrEmpowered.resolve.js';
import { hasPendingCoveringFireChoice } from './coveringFireChoice.resolve.js';
import { hasPendingMoveVillainChoice } from './moveVillainChoice.resolve.js';
import { hasPendingSplitFaceChoice } from './splitFaceChoice.resolve.js';
import { hasPendingCountScaledChoice } from './countScaledChoice.resolve.js';
import { hasPendingUndercoverChoice } from './undercover.resolve.js';
import { hasPendingReturnZeroCostDiscard } from './resolveReturnZeroCostDiscard.js';
import { hasPendingDiscardToPlay } from './resolveDiscardToPlay.js';
import { hasPendingReturnOnDiscard } from './resolveReturnOnDiscard.js';
import { hasPendingGiveHqHeroChoice } from './giveHqHeroChoice.resolve.js';
import { hasPendingCopyPowersChoice } from './copyPowersChoice.resolve.js';
import { hasPendingSeatChoice } from './seatChoice.resolve.js';
import { pushLog } from '../log/logPush.js';
import { composeHealNarrative } from '../events/notableEvents.compose.js';
/**
 * Returns whether the current player has already used the Healing ability this
 * turn. Read by fightVillain / recruitHero / fightMastermind to enforce the
 * reverse lock — a player who heals may not fight or recruit for the rest of the
 * turn (D-24180). Mirrors the hasPending*(G) predicate shape.
 *
 * @param G - The game state.
 * @returns True when G.hasHealedThisTurn is set.
 */
export function hasHealedThisTurn(G) {
    return G.hasHealedThisTurn === true;
}
/**
 * Uses the Wound "Healing" ability: KOs every Wound in the current player's hand.
 *
 * Follows the non-core move contract. On a successful heal, each Wound moves from
 * the player's hand to G.ko, G.hasHealedThisTurn is set, and one line is appended
 * to G.messages. Any blocked, out-of-stage, already-acted, or no-Wounds call
 * returns void with no mutation. Moves never throw.
 *
 * @param context - boardgame.io move context with G, ctx.
 */
export function healWounds({ G, ctx }) {
    // Step 2: Stage gate (non-core move, internal gating)
    // why: Healing is a your-turn main-action-window action, mirroring fightVillain
    // / recruitHero — non-core moves gate internally per the WP-014A precedent.
    if (G.currentStage !== 'main')
        return;
    // why: block-all guard (D-24008) — while a KO-a-Hero choice is pending the board
    // is frozen; healWounds returns with no side effects. This is the identical
    // block-all cluster the fight/recruit moves carry, so a pending choice freezes
    // Healing too.
    if (hasPendingKoHeroChoice(G))
        return;
    // why: block-all guard (D-24282) — a pending Doombot scry-KO choice freezes the
    // board until the player picks which revealed card to KO.
    if (hasPendingScryKoChoice(G))
        return;
    // why: WP-603 / D-24413 — block-all guard: a pending Melter Fight KO/keep choice
    // freezes the board until the fighting player resolves every revealed deck top.
    if (hasPendingMelterKoChoice(G))
        return;
    // why: WP-695 / D-24512 — block-all guards for the two interactive core mastermind
    // tactics (Ruthless Dictator scry-3 / Electromagnetic Bubble X-Men pick).
    if (hasPendingRuthlessDictatorChoice(G))
        return;
    if (hasPendingElectromagneticBubbleChoice(G))
        return;
    // why: block-all guard (WP-476 / D-24284) — a pending discard-to-limit choice
    // freezes the board until the current player picks which cards to discard.
    if (hasPendingDiscardChoice(G))
        return;
    if (hasPendingPutCardsOnDeckChoice(G))
        return;
    if (hasPendingReorderChoice(G))
        return; // why: WP-479 / D-24286 block-all guard
    if (hasPendingDefeatChoice(G))
        return; // why: WP-486 / D-24291 block-all guard
    if (hasPendingKoDiscardChoice(G))
        return; // why: WP-693 / D-24510 block-all guard
    // why: block-all guard (D-24019) — optional-KO-reward choice pending.
    if (hasPendingPlayVillainTopChoice(G))
        return; // why: WP-663 / D-24474 — block-all guard (Shadowed Thoughts play-villain-top choice)
    if (hasPendingOptionalKoReward(G))
        return;
    if (hasPendingSmashDiscard(G))
        return; // why: WP-676 / D-24492 — block-all guard (Smash discard-for-attack choice)
    if (hasPendingPutHandOnDeckTop(G))
        return; // why: WP-700 / D-24519 — block-all guard (put-a-hand-card-on-deck-top choice)
    if (hasPendingRevealTopDispose(G))
        return; // why: WP-702 / D-24521 — block-all guard (reveal-top discard-or-keep choice)
    if (hasPendingRevealThreeAssign(G))
        return; // why: WP-753 / D-24580 — block-all guard (reveal-three draw / discard / KO assignment)
    if (hasPendingDoOver(G))
        return; // why: WP-681 / D-24498 — block-all guard (Do-Over accept/decline choice)
    // why: block-all — pendingVictoryPileCardPick must be resolved first (D-24067)
    if (hasPendingVictoryPileCardPick(G))
        return;
    // why: block-all — pendingDrawOrEmpowered must be resolved first (D-24069)
    if (hasPendingDrawOrEmpowered(G))
        return;
    // why: block-all guard (WP-719 / D-24541) — a pending Covering Fire choice freezes the board.
    if (hasPendingCoveringFireChoice(G))
        return;
    // why: block-all guard (WP-795 / D-24664) — a pending Spinning Cyclone move-a-Villain choice freezes the board.
    if (hasPendingMoveVillainChoice(G))
        return;
    // why: block-all guard (WP-724 / D-24546) — a pending split-face "choose a side" freezes the board.
    if (hasPendingSplitFaceChoice(G))
        return;
    if (hasPendingCountScaledChoice(G))
        return;
    // why: block-all — pendingUndercoverChoice must be resolved before any other action (WP-678 / D-24494)
    if (hasPendingUndercoverChoice(G))
        return;
    // why: block-all — pendingReturnZeroCostDiscard must be resolved first (D-24139)
    if (hasPendingReturnZeroCostDiscard(G))
        return;
    // why: block-all — pendingDiscardToPlay must be resolved first (WP-383 / D-24184)
    if (hasPendingDiscardToPlay(G))
        return;
    // why: block-all — pendingReturnOnDiscard must be resolved before any other action (WP-498 / D-24301)
    if (hasPendingReturnOnDiscard(G))
        return;
    // why: block-all — pendingGiveHqHeroChoice (Paibok Fight) must be resolved first (WP-532 / D-24343)
    if (hasPendingGiveHqHeroChoice(G))
        return;
    // why: block-all — pendingCopyPowersChoice (Rogue's Copy Powers) must be resolved first (WP-535 / D-24345)
    if (hasPendingCopyPowersChoice(G))
        return;
    if (hasPendingSeatChoice(G))
        return; // why: WP-684 / D-24501 — block-all (non-active/multi-seat pending choice)
    // why: D-24179 — Healing is barred once the player has recruited or fought this
    // turn ("If you don't recruit or fight anything on your turn, you may KO all the
    // Wounds from your hand"). hasActedThisTurn is set on a successful fightVillain /
    // recruitHero / fightMastermind.
    if (G.hasActedThisTurn === true)
        return;
    const playerZones = G.playerZones[ctx.currentPlayer];
    if (!playerZones)
        return;
    // why: partition the hand into surviving (non-Wound) cards and a Wound count in
    // a single explicit pass — no .reduce() / .filter() per the zone-op style.
    const survivingHand = [];
    let woundsToKo = 0;
    for (const cardId of playerZones.hand) {
        if (cardId === WOUND_EXT_ID) {
            woundsToKo += 1;
        }
        else {
            survivingHand.push(cardId);
        }
    }
    // why: no Wounds in hand means there is nothing to heal — deterministic no-op.
    if (woundsToKo === 0)
        return;
    // Step 3: Mutate G — KO every Wound to G.ko, keep the rest of the hand, set the
    // reverse-lock flag.
    // why: koCard appends one card and every KO'd Wound shares WOUND_EXT_ID, so KO
    // one copy per counted Wound.
    for (let woundIndex = 0; woundIndex < woundsToKo; woundIndex += 1) {
        G.ko = koCard(G.ko, WOUND_EXT_ID);
        // why: WP-853 / D-24789 — one owned KO per healed Wound (Absorb Energies).
        recordOwnedCardKoed(G, ctx.currentPlayer, WOUND_EXT_ID, 'hand');
    }
    playerZones.hand = survivingHand;
    // why: D-24180 — once the player heals they may not fight or recruit for the rest
    // of the turn; fightVillain / recruitHero / fightMastermind read this via
    // hasHealedThisTurn(G).
    G.hasHealedThisTurn = true;
    pushLog(G, `Player ${ctx.currentPlayer} used Healing: KO'd ${woundsToKo} Wound(s) from hand.`);
    // why: WP-381 / D-24182 — emit the healResolved notable event LAST (after the
    // reverse-lock flag and the log push), observing settled state — the
    // fightVillain emission precedent. Unconditional push; setup guarantees
    // G.notableEvents. G.messages is not projected to clients, so this event is
    // what drives the center-screen "Healed" overlay.
    G.notableEvents.push({
        type: 'healResolved',
        playerId: ctx.currentPlayer,
        woundsHealed: woundsToKo,
        narrative: composeHealNarrative(woundsToKo),
    });
}
//# sourceMappingURL=healWounds.js.map